import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import organizationsService from '../../services/crm/organizationsService';
import * as telemarketingService from '../../services/crm/telemarketingService';
import { format } from 'date-fns';

const PhoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const PlayIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const PauseIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const TrendingUpIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const UsersIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  archived: 'bg-gray-100 text-gray-500'
};

const TelemarketingCampaigns: React.FC = () => {
  const { user } = useAuth();
  const { activeOrgId, setActiveOrgId, isBootstrapped, setBootstrapped } = useCrmOrgStore();
  const navigate = useNavigate();
  
  const [campaigns, setCampaigns] = useState<telemarketingService.TelemarketingCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(!isBootstrapped);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [stats, setStats] = useState<{
    total: number;
    active: number;
    calls_today: number;
    conversion_rate: number;
  }>({ total: 0, active: 0, calls_today: 0, conversion_rate: 0 });
  const [minutesUsage, setMinutesUsage] = useState<telemarketingService.MinutesUsage | null>(null);
  const [minutesLoading, setMinutesLoading] = useState(false);
  const [minutesError, setMinutesError] = useState<string | null>(null);
  const [phoneStatus, setPhoneStatus] = useState<telemarketingService.PhoneNumberStatus | null>(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [provisioningNumber, setProvisioningNumber] = useState(false);
  
  // Address modal for UK/EU phone number provisioning
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState({
    customerName: '',
    street: '',
    city: '',
    region: '',
    postalCode: '',
    countryCode: 'GB'
  });

  // Bootstrap organization on mount if not already done
  useEffect(() => {
    const bootstrap = async () => {
      if (!user?.id) return;
      if (activeOrgId && isBootstrapped) {
        setBootstrapping(false);
        return;
      }

      try {
        const response = await organizationsService.list();
        const list = response.data || [];

        if (list.length === 0) {
          // Create default organization
          const created = await organizationsService.create('My Organization');
          if (created.data) {
            setActiveOrgId(created.data.id);
          }
        } else if (!activeOrgId) {
          // Use first organization
          setActiveOrgId(list[0].id);
        }
      } catch (err: any) {
        console.error('Failed to bootstrap organization:', err);
        toast.error('Failed to load organization');
      } finally {
        setBootstrapped(true);
        setBootstrapping(false);
      }
    };

    bootstrap();
  }, [user?.id, activeOrgId, isBootstrapped, setActiveOrgId, setBootstrapped]);

  // Load campaigns
  const loadCampaigns = async () => {
    if (!activeOrgId) return;
    
    setLoading(true);
    try {
      const result = await telemarketingService.listCampaigns(activeOrgId, {
        status: statusFilter || undefined,
        limit: 50,
        offset: 0
      });
      
      setCampaigns(result.data || []);
      
      // Calculate stats
      const total = (result.total ?? result.count ?? 0) as number;
      const active = (result.data || []).filter((c: any) => c.status === 'active').length;
      
      setStats({
        total,
        active,
        calls_today: 0, // TODO: Get from analytics
        conversion_rate: 0 // TODO: Get from analytics
      });
    } catch (error: any) {
      console.error('Failed to load campaigns:', error);
      toast.error(error.response?.data?.error || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, [activeOrgId, statusFilter]);

  // Load minutes usage when organization changes
  useEffect(() => {
    const loadUsage = async () => {
      if (!activeOrgId) return;
      setMinutesLoading(true);
      setMinutesError(null);
      try {
        const usage = await telemarketingService.getMinutesUsage(activeOrgId);
        setMinutesUsage(usage);
      } catch (error: any) {
        console.error('Failed to load minutes usage:', error);
        setMinutesError(error.response?.data?.error || 'Unable to load minutes usage');
      } finally {
        setMinutesLoading(false);
      }
    };

    loadUsage();
  }, [activeOrgId]);

  const loadPhoneNumber = async () => {
    if (!activeOrgId) return;
    setPhoneLoading(true);
    setPhoneError(null);
    try {
      const status = await telemarketingService.getOrganizationPhoneNumber(activeOrgId);
      setPhoneStatus(status);
    } catch (error: any) {
      console.error('Failed to load phone number:', error);
      setPhoneError(error.response?.data?.error || 'Unable to load phone number');
    } finally {
      setPhoneLoading(false);
    }
  };

  useEffect(() => {
    loadPhoneNumber();
  }, [activeOrgId]);

  const renderMinutesCard = () => {
    if (minutesLoading) {
      return (
        <div className="bg-white rounded-lg shadow p-6 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-6 bg-gray-200 rounded w-1/2"></div>
        </div>
      );
    }

    if (minutesError) {
      return (
        <div className="bg-white rounded-lg shadow p-6 border border-yellow-200">
          <div className="text-sm text-gray-500 mb-1">Minutes Remaining</div>
          <div className="text-sm text-yellow-700">{minutesError}</div>
        </div>
      );
    }

    if (!minutesUsage) {
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Minutes Remaining</div>
          <div className="text-xl text-gray-500">Not available</div>
        </div>
      );
    }

    const percent = minutesUsage.limit > 0
      ? Math.min(100, Math.max(0, (minutesUsage.remaining / minutesUsage.limit) * 100))
      : 0;
    const usageLabel = `${minutesUsage.remaining.toFixed(0)} / ${minutesUsage.limit.toFixed(0)} mins`;
    const lowMinutes = minutesUsage.remaining <= minutesUsage.limit * 0.1;

    return (
      <div className={`rounded-lg shadow p-6 ${lowMinutes ? 'bg-red-50 border border-red-200' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-gray-500">Minutes Remaining</div>
          <span className={`text-xs font-medium px-2 py-1 rounded ${minutesUsage.available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {minutesUsage.available ? 'Available' : 'Exhausted'}
          </span>
        </div>
        <div className="text-3xl font-bold text-gray-900 mb-2">{usageLabel}</div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className={`${lowMinutes ? 'bg-red-500' : 'bg-green-500'} h-2 rounded-full`}
            style={{ width: `${percent}%` }}
          ></div>
        </div>
        {!minutesUsage.available && (
          <p className="mt-3 text-sm text-red-600">
            Call minutes exhausted. Purchase an additional block to continue dialing.
          </p>
        )}
        {minutesUsage.available && lowMinutes && (
          <p className="mt-3 text-sm text-red-600">
            Less than 10% of minutes remain. Consider purchasing another block.
          </p>
        )}
      </div>
    );
  };

  // Countries that require a business address for phone numbers
  const COUNTRIES_REQUIRING_ADDRESS = ['GB', 'DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'AT', 'CH', 'IE', 'PT', 'SE', 'NO', 'DK', 'FI', 'PL'];

  const handleProvisionNumber = async () => {
    if (!activeOrgId) return;
    if (phoneStatus?.hasNumber) {
      toast.success('Phone number already provisioned');
      return;
    }

    // For UK/EU, show address modal first
    if (COUNTRIES_REQUIRING_ADDRESS.includes(addressForm.countryCode)) {
      setShowAddressModal(true);
      return;
    }

    await doProvisionNumber();
  };

  const doProvisionNumber = async () => {
    if (!activeOrgId) return;
    
    setProvisioningNumber(true);
    setShowAddressModal(false);
    
    try {
      // Include address details for UK/EU numbers
      const payload: any = { 
        organization_id: activeOrgId,
        country_code: addressForm.countryCode
      };
      
      if (COUNTRIES_REQUIRING_ADDRESS.includes(addressForm.countryCode)) {
        payload.address = {
          customerName: addressForm.customerName,
          street: addressForm.street,
          city: addressForm.city,
          region: addressForm.region,
          postalCode: addressForm.postalCode,
          countryCode: addressForm.countryCode
        };
      }
      
      await telemarketingService.provisionPhoneNumber(payload);
      toast.success('Phone number provisioned successfully!');
      await loadPhoneNumber();
    } catch (error: any) {
      console.error('Failed to provision phone number:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to provision phone number');
    } finally {
      setProvisioningNumber(false);
    }
  };

  const renderPhoneNumberCard = () => {
    if (phoneLoading) {
      return (
        <div className="bg-white rounded-lg shadow p-6 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-6 bg-gray-200 rounded w-2/3"></div>
        </div>
      );
    }

    if (phoneError) {
      return (
        <div className="bg-white rounded-lg shadow p-6 border border-yellow-200">
          <div className="text-sm text-gray-500 mb-2">Outbound Caller ID</div>
          <div className="text-sm text-yellow-700 mb-3">{phoneError}</div>
          <button
            onClick={loadPhoneNumber}
            className="px-4 py-2 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
          >
            Retry
          </button>
        </div>
      );
    }

    if (phoneStatus?.hasNumber && phoneStatus.phoneNumber) {
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-500">Outbound Caller ID</div>
            <span className="text-xs font-medium px-2 py-1 rounded bg-green-100 text-green-700">
              Provisioned
            </span>
          </div>
          <div className="text-3xl font-bold text-gray-900">{phoneStatus.phoneNumber}</div>
          <p className="mt-3 text-sm text-gray-600">
            Calls originate from this Twilio number.
          </p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow p-6 border border-purple-100">
        <div className="text-sm text-gray-500 mb-2">Outbound Caller ID</div>
        <p className="text-sm text-gray-700 mb-4">
          Provision a dedicated Twilio number so campaigns can start dialing immediately.
        </p>
        <button
          onClick={handleProvisionNumber}
          disabled={provisioningNumber}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60"
        >
          {provisioningNumber ? 'Provisioning…' : 'Provision Number'}
        </button>
      </div>
    );
  };

  const handleStartCampaign = async (id: string) => {
    if (!activeOrgId) return;
    
    try {
      await telemarketingService.startCampaign(id, activeOrgId);
      toast.success('Campaign started');
      loadCampaigns();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to start campaign');
    }
  };

  const handlePauseCampaign = async (id: string) => {
    if (!activeOrgId) return;
    
    try {
      await telemarketingService.pauseCampaign(id, activeOrgId);
      toast.success('Campaign paused');
      loadCampaigns();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to pause campaign');
    }
  };

  // Show loading while bootstrapping organization
  if (bootstrapping || !activeOrgId) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-pulse text-gray-500">Initializing telemarketing module...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">AI Telemarketing</h1>
            <Link
              to="/help?article=crm-telemarketing"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
            >
              ❓ Telemarketing help
            </Link>
          </div>
          <p className="text-gray-600 mt-1">Automated cold calling with AI-powered conversations</p>
        </div>
        <button
          onClick={() => navigate('/crm/telemarketing/create')}
          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center gap-2"
        >
          <PhoneIcon className="w-5 h-5" />
          New Campaign
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Total Campaigns</div>
          <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Active</div>
          <div className="text-3xl font-bold text-green-600">{stats.active}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Calls Today</div>
          <div className="text-3xl font-bold text-blue-600">{stats.calls_today}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Conversion Rate</div>
          <div className="text-3xl font-bold text-purple-600">{stats.conversion_rate.toFixed(1)}%</div>
        </div>
        {renderMinutesCard()}
        {renderPhoneNumberCard()}
      </div>

      {minutesUsage && minutesUsage.status === 'no_tracking' && (
        <div className="mb-6 p-4 border border-yellow-200 rounded-lg bg-yellow-50 text-sm text-yellow-800">
          Usage tracking is not initialized for this organization. Provision a telemarketing plan to enable minutes monitoring.
        </div>
      )}

      {/* Status Filters */}
      <div className="mb-6 flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Campaign Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12">
          <PhoneIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
          <p className="text-gray-600 mb-6">Create your first AI telemarketing campaign to get started</p>
          <button
            onClick={() => navigate('/crm/telemarketing/create')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/crm/telemarketing/campaigns/${campaign.id}`)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {campaign.name}
                  </h3>
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${statusColors[campaign.status]}`}>
                    {campaign.status}
                  </span>
                </div>
              </div>

              {/* Description */}
              {campaign.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {campaign.description}
                </p>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t border-gray-100">
                <div>
                  <div className="text-xs text-gray-500">Objective</div>
                  <div className="text-sm text-gray-500 mb-2">
                    {campaign.description || 'No description'}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-4 text-sm text-gray-600">
                      <span>📞 {campaign.max_attempts || 3} attempts</span>
                      <span>🎯 {campaign.objective || 'General'}</span>
                    </div>
                    <div className="flex gap-2">
                      {campaign.status === 'active' ? (
                        <button
                          onClick={() => handlePauseCampaign(campaign.id)}
                          className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                        >
                          Pause
                        </button>
                      ) : campaign.status === 'draft' || campaign.status === 'paused' ? (
                        <button
                          onClick={() => handleStartCampaign(campaign.id)}
                          className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Start
                        </button>
                      ) : null}
                      <button 
                        onClick={() => navigate(`/crm/telemarketing/${campaign.id}/calls`)}
                        className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                      >
                        Monitor
                      </button>
                      <button 
                        onClick={() => navigate(`/crm/telemarketing/${campaign.id}/analytics`)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Analytics
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => navigate(`/crm/telemarketing/campaigns/${campaign.id}`)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                >
                  View Details
                </button>
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                Created {format(new Date(campaign.created_at), 'MMM d, yyyy')}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Phone Number Selection Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Choose Your Phone Number</h3>
            <p className="text-sm text-gray-600 mb-6">
              Select the type of phone number for your telemarketing campaigns.
            </p>
            
            {/* UK Local Number */}
            <div className="p-4 border-2 border-purple-200 rounded-lg mb-4 hover:border-purple-400 transition-colors">
              <div className="flex items-start gap-3">
                <div className="text-2xl">🇬🇧</div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">UK Local Number</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Geographic number that displays as a local UK caller ID. Best for calling UK prospects.
                  </p>
                  <button
                    onClick={async () => {
                      setProvisioningNumber(true);
                      setShowAddressModal(false);
                      try {
                        await telemarketingService.provisionPhoneNumber({ 
                          organization_id: activeOrgId!,
                          country_code: 'GB',
                          number_type: 'local'
                        });
                        toast.success('UK phone number provisioned successfully!');
                        await loadPhoneNumber();
                      } catch (error: any) {
                        toast.error(error.message || 'Failed to provision number');
                      } finally {
                        setProvisioningNumber(false);
                      }
                    }}
                    disabled={provisioningNumber}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                  >
                    {provisioningNumber ? 'Provisioning...' : 'Get UK Number'}
                  </button>
                </div>
              </div>
            </div>
            
            {/* US Toll-Free */}
            <div className="p-4 border-2 border-gray-200 rounded-lg hover:border-gray-400 transition-colors">
              <div className="flex items-start gap-3">
                <div className="text-2xl">🇺🇸</div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">US Toll-Free Number</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    International toll-free number. Can call UK numbers but displays as US caller ID.
                  </p>
                  <button
                    onClick={async () => {
                      setProvisioningNumber(true);
                      setShowAddressModal(false);
                      try {
                        await telemarketingService.provisionPhoneNumber({ 
                          organization_id: activeOrgId!,
                          country_code: 'US',
                          number_type: 'toll_free'
                        });
                        toast.success('US toll-free number provisioned successfully!');
                        await loadPhoneNumber();
                      } catch (error: any) {
                        toast.error(error.message || 'Failed to provision number');
                      } finally {
                        setProvisioningNumber(false);
                      }
                    }}
                    disabled={provisioningNumber}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm"
                  >
                    {provisioningNumber ? 'Provisioning...' : 'Get US Toll-Free'}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAddressModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelemarketingCampaigns;
